import { createContext, useCallback, useContext } from "react";
import { useNavigate } from "react-router";
import { isEmpty } from "lodash";

import { routesConfig, routesMap } from "./routesConfig";
import { isExternalUrl } from "../../helpers/UrlHelper";
import { unlocalizePathname } from "../Utils/localizedLocation";
import { useContextualPathFor } from "../Routing/pathFor";

const URL_REGEX = /^(https?:)?\/\//;

const RoutesContext = createContext({});
export const useRoutesContext = () => useContext(RoutesContext) || {};

function RoutesContextProvider({ children }) {
  const pathFor = useContextualPathFor();

  return (
    <RoutesContext.Provider value={{ routes: routesConfig, resolve, pathFor }}>
      {children}
    </RoutesContext.Provider>
  );
}

export default RoutesContextProvider;

// This is a hook that can be used by components to make the user navigate to a named
// route, the way you would use `redirect_to()` in Rails (for named routes only).
//
// The idea here is to provide a simple helper which handles all the context and redux
// related stuff without having to expose it to the component. This simplifies the actual
// component code and avoids to manually dispatch the react-connected-router actions
// manually.
//
// Example :
//
//  const navigateTo = useNavigateTo();
//  const handleClick = () => navigateTo("participativeModule", { participativeModuleSlug });
//
//  return <Button onClick={handleClick} />
//
export const useNavigateTo = () => {
  const navigate = useNavigate();
  const pathFor = useContextualPathFor();

  const navigateTo = useCallback(
    (routeName, params = {}, options = {}) => {
      const isURL = routeName.match(URL_REGEX);
      const isPath = routeName.match(/^\//);

      const path =
        (isPath || isURL) && isEmpty(params) ? routeName : pathFor(routeName, params);

      if (options.target || isExternalUrl(path)) {
        options.target = options.target || "_blank";
        window.open(path, options.target);
      } else {
        const unlocalizedPathFor = url => {
          // Since we stay on the same domain, we remove the host part of the URL, as it's
          // not supported by the react-router's navigate method.
          const pathWithoutDomain = url.match(URL_REGEX)
            ? url.split(new URL(url).host)[1]
            : url;

          return unlocalizePathname(pathWithoutDomain);
        };

        const finalPath = unlocalizedPathFor(path);
        const currentPath = unlocalizedPathFor(window.location.href);

        // If the current path is the same as the final target path, don't navigate as
        // it won't change anything, except pushing a new entry to the history.
        if (!options.force && currentPath === finalPath) return;

        const navigateOptions = {};
        if (options.replace) navigateOptions.replace = true;

        navigate(finalPath, navigateOptions);
      }
    },
    [navigate, pathFor]
  );

  return navigateTo;
};

export function withNavigateTo(WrappedComponent) {
  return function WithNavigateTo(props) {
    const navigateTo = useNavigateTo();
    return <WrappedComponent {...props} navigateTo={navigateTo} />;
  };
}

export function resolve(path) {
  const pathWithoutLocale = unlocalizePathname(path);

  const resolvedRoute = Object.values(routesMap).find(route => {
    const matches = pathWithoutLocale.match(regexFromRoutePath(route.path));

    if (!matches || !route.paramConstraints) return matches;

    const params = extractParamsFrom(pathWithoutLocale, route);

    return Object.keys(route.paramConstraints).every(paramName => {
      const regex = route.paramConstraints[paramName];

      return params[paramName].match(regex);
    });
  });

  if (!resolvedRoute) return;

  return {
    ...resolvedRoute,
    params: extractParamsFrom(pathWithoutLocale, resolvedRoute)
  };
}

function extractParamsFrom(path, route) {
  const segments = path.split("/");

  return route.path.split("/").reduce((params, member, index) => {
    const match = member.match(/^:(\w+)/);
    if (!match) return { ...params };

    const paramName = match[1];
    const value = segments[index];

    return { ...params, [paramName]: value };
  }, {});
}

// TODO : Better locale handling, this works for now since we have no
//        route prefix with 2 lowercase letters ...
function regexFromRoutePath(path) {
  const routePath = path
    // Replace :param by a regex matching anything except /
    .replace(/:\w+/g, "[^/]+")
    // Remove locale prefix if any
    .replace(/^\/[a-z]{2}(\/.+)/, "$1")
    // Remove trailing slash as it's added as optional in the regex below
    .replace(/\/$/, "");

  return new RegExp(`^${routePath}/?$`);
}
